import type { ReviewAction, ReviewActionName } from "../../../api/content/reviewActions";
import type { Review } from "../../../api/content/reviewDecoders";
import type { BumpChoices } from "../../versions/merge/versionConfirmation";
import { mergeReady, type ReviewPageState } from "./reviewPageState";

/** What the decision bar offers: decide on the head (R-1), merge it (R-2), or resubmit. */
export type DecisionAction = Extract<ReviewActionName, "approve" | "request_changes" | "scrap" | "merge" | "resubmit">;

/** What the caller types when deciding. */
export interface DecisionInput {
  readonly comment: string;
  readonly note: string;
  /** For a merge: the change line the new version labels show (ADR-0033 D3). */
  readonly changeLine: string;
  /** For a merge: where the person changed DMW's proposed version bumps. */
  readonly bumps: BumpChoices;
}

const BAR_ORDER: readonly DecisionAction[] = ["resubmit", "request_changes", "approve", "merge", "scrap"];

/** Module B's decision on the action for the caller; `undefined` when it isn't served. */
export function servedAction(review: Review, action: ReviewActionName): ReviewAction | undefined {
  return review.actions.find((served) => served.action === action);
}

/** Whether module B allows the caller the action now, decided on the change set's own project. */
export function allows(review: Review, action: ReviewActionName): boolean {
  return servedAction(review, action)?.refusal === null;
}

/**
 * The actions the bar shows, in its order: those the caller may take, and those that wait for something first
 * (P-B: unavailable, with why). One the change set's state refuses isn't shown; one refused for who the caller is
 * isn't either, and "curates none" is said once, under the bar (`curatesNone`).
 */
export function offeredActions(review: Review): readonly DecisionAction[] {
  return BAR_ORDER.filter((action) => {
    const served = servedAction(review, action);
    return served !== undefined && (served.refusal === null || served.concern === "prerequisite");
  });
}

/** Whether the server refuses the caller a decision because they curate none of the changed items. */
export function curatesNone(review: Review): boolean {
  return review.actions.some((served) => served.refusal === "curates_none");
}

/** Request changes, scrap and merge say why (FR-VERSION-3); an approval may. */
export function commentRequired(action: DecisionAction): boolean {
  return action === "request_changes" || action === "scrap" || action === "merge";
}

/** Whether the action can be started now, before anything is typed. */
export function actionAvailable(action: DecisionAction, review: Review, state: ReviewPageState): boolean {
  if (action === "merge") {
    return mergeReady(review, { ...state, acknowledged: true });
  }
  return allows(review, action);
}

/** Whether the action can be sent with what was typed: a needed comment, and for a merge the acknowledgement and its note. */
export function actionReady(action: DecisionAction, review: Review, state: ReviewPageState, input: DecisionInput): boolean {
  if (!actionAvailable(action, review, state)) {
    return false;
  }
  if (commentRequired(action) && input.comment.trim() === "") {
    return false;
  }
  if (action === "merge") {
    return mergeReady(review, state) && (!review.openFindings.needsAcknowledgement || input.note.trim() !== "");
  }
  return true;
}

/** A blocked head leads with send-back; an uncovered item my approval counts for leads with approval before merge. */
export function primaryAction(actions: readonly DecisionAction[], review: Review, state: ReviewPageState): DecisionAction | null {
  if (review.mergeBlocks.blocked && actions.includes("request_changes") && actionAvailable("request_changes", review, state)) {
    return "request_changes";
  }
  const approvalNeeded = review.summary.uncovered.some((item) => review.summary.callerCovered.some((covered) => covered.kind === item.kind && covered.itemId === item.itemId));
  if (approvalNeeded && actions.includes("approve") && actionAvailable("approve", review, state)) {
    return "approve";
  }
  if (actions.includes("merge") && actionAvailable("merge", review, state)) {
    return "merge";
  }
  return actions.find((action) => action === "resubmit" || action === "approve") ?? null;
}
